import { Component, Vue } from "vue-property-decorator";
import UlContentHeader from "@/components/UlContentHeader.vue";
import UlBreadcrumbs from "@/components/UlBreadcrumbs.vue";
import UIDataTable from "@/components/UIDataTable.vue";
import UIDialogDelete from "@/components/UlDialogDelete.vue";
import {
  DEFAULT_TABLE_OPTIONS,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC,
  TableOptions
} from "@/api/request";
import { SearchRequest } from "@/api/role-setting/request";
import { DeleteRequest } from "@/api/role-setting/request";
import { SearchItem } from "@/api/role-setting/response";
import RoleSettingSearch from "@/store/role-setting/search";
import RoleSettingModify from "@/store/role-setting/modify";
import RoleSettingShopGet from "@/store/role-setting-shop/get";
import Flash, { ErrorAlert, SuccessAlert } from "@/store/common/flash";

@Component({
  components: { UlContentHeader, UlBreadcrumbs, UIDataTable, UIDialogDelete }
})
export default class Index extends Vue {
  // ------------
  // 固定値
  // ------------
  // タイトル
  headingMain = "権限ロール";
  headingSub = "Authority";
  breadCrumbs = [
    { text: "設定", disabled: true },
    { text: "権限ロール", disabled: true },
    { text: "店舗一覧", disabled: true }
  ];
  // テーブルヘッダ（UIDataTableコンポーネントに渡す）
  tableHeaders = [
    { text: "店舗ID", value: "shopId" },
    { text: "店舗名", value: "shopName" },
    { text: "権限ロール名", value: "roleName" },
    { text: "", value: "space", sortable: false },
    {
      label: "削除",
      text: "",
      value: "delete",
      sortable: false
    },
    {
      label: "修正",
      text: "",
      value: "edit",
      sortable: false
    }
  ];

  menuProps = {
    closeOnClick: false,
    closeOnContentClick: false,
    disableKeys: true,
    openOnClick: false,
    maxHeight: 304
  };

  // ------------
  // 変動値
  // ------------

  // 削除ダイアログの表示有無
  showDialog = false;

  // 削除される権限ロール名
  deletingRoleName = "";

  // 削除される権限ロールID
  deletingRoleId = "";

  // ローディングステータス
  isLoading = false;

  // 検索入力オプション
  inputOptions = {
    searchShopId: null as number | null,
    selectShopIds: null as number[] | null
  };

  // テーブル検索オプション（UIDataTableコンポーネントに渡す）
  tableOptions: TableOptions = {
    page: 1,
    displayCount: 20,
    sortKey: "shopId",
    sortOrder: SORT_ORDER_ASC
  };

  // ------------

  /**
   * テーブルに表示するアイテムリスト（UIDataTableコンポーネントに渡す）
   */
  get tableItems() {
    return RoleSettingSearch.getItems;
  }

  /**
   * 総件数（UIDataTableコンポーネントに渡す）
   */
  get totalCount() {
    return RoleSettingSearch.getTotalCount;
  }

  get shopItems() {
    return RoleSettingShopGet.getItems;
  }

  get writeFlg() {
    return RoleSettingShopGet.getWriteFlg;
  }

  /**
   * createdライフサイクルフック（UIDataTableコンポーネントに渡す）
   */
  async created() {
    await RoleSettingSearch.restore(this.inputOptions as SearchRequest);
    const request = RoleSettingSearch.getSearchRequest;
    // 検索入力オプション
    request.searchShopId &&
      (this.inputOptions.searchShopId = request.searchShopId);
    request.selectShopIds &&
      (this.inputOptions.selectShopIds = request.selectShopIds);

    // 検索テーブルオプション
    request.page && (this.tableOptions = request);
    await this.search();
  }

  /**
   * beforeDestroyライフサイクルフック
   */
  async beforeDestroy() {
    await RoleSettingShopGet.clearResponse();
    await RoleSettingSearch.clearResponse();
    await RoleSettingModify.clearResponse();
  }

  /**
   * ページング変更コールバック
   *
   * @param tableOptions TableOptions
   */
  async tableChangeCallback(tableOptions: TableOptions) {
    // 子コンポーネントへpagingOptionsの変更が通知される
    this.tableOptions = tableOptions;
    if (this.tableOptions.sortKey == null) {
      this.tableOptions.sortKey = "shopId";
      this.tableOptions.sortOrder = SORT_ORDER_ASC;
    }
    await this.search();
  }

  /**
   * 検索ボタンがクリックされた際のコールバック
   */
  async searchClickCallback() {
    await Flash.clear();
    await this.search();
  }

  /**
   * テーブル行のアクションボタンがクリックされた際のコールバック
   */
  async actionClickCallback() {}

  /**
   * テーブル行の編集ボタンがクリックされた際のコールバック
   *
   * @param item 選択行のAdminItem
   */
  async editClickCallback(item: SearchItem) {
    await this.$router.push({
      name: "authority-edit",
      params: { id: item.id }
    });
  }

  /**
   * テーブル行の削除ボタンがクリックされた際のコールバック
   *
   * @param item 選択行のAdminItem
   */
  deleteClickCallback(item: SearchItem) {
    this.showDialog = true;
    this.deletingRoleId = item.id;
    this.deletingRoleName = item.roleName;
  }

  /**
   * ダイアログの削除のコールバック
   */
  async doDeleteOnDialog() {
    await Flash.clear();
    this.showDialog = false;
    if (!this.deletingRoleId) {
      return;
    }

    this.isLoading = true;
    await RoleSettingModify.deleteOne({
      id: this.deletingRoleId
    } as DeleteRequest);
    this.isLoading = false;
    if (RoleSettingModify.isSuccess) {
      await Flash.setSuccessNow({
        message: "権限ロールを削除しました。",
        consumePath: ""
      } as SuccessAlert);
      await this.search();
    } else {
      await Flash.setErrorNow({
        message: RoleSettingModify.getMessage,
        showReloadButton: false
      } as ErrorAlert);
    }
  }

  /**
   * 店舗情報の一覧を取得する処理
   */
  async fetchShopList(): Promise<boolean> {
    if (RoleSettingShopGet.isSuccess) {
      return true;
    }

    await RoleSettingShopGet.get();
    if (!RoleSettingShopGet.isSuccess) {
      await Flash.setErrorNow({
        message: RoleSettingShopGet.getMessage,
        showReloadButton: false
      } as ErrorAlert);
    }
    return RoleSettingShopGet.isSuccess;
  }

  /**
   * 検索処理
   */
  async search() {
    this.isLoading = true;
    let isSuccess = await this.fetchShopList();
    if (!isSuccess) {
      this.isLoading = false;
      return;
    }

    let request = this.createRequest();
    await RoleSettingSearch.search(request);
    if (!RoleSettingSearch.isSuccess) {
      await Flash.setErrorNow({
        message: RoleSettingSearch.getMessage,
        showReloadButton: true
      } as ErrorAlert);
    }
    this.isLoading = false;
  }

  goToNew() {
    this.$router.push({ name: "authority-new" }).then();
  }

  /**
   * dataから検索用のリクエストを作成する
   */
  private createRequest(): SearchRequest {
    const request = this.tableOptions as SearchRequest;
    if (this.inputOptions.searchShopId) {
      request.searchShopId = this.inputOptions.searchShopId;
    } else {
      delete request.searchShopId;
    }

    if (this.inputOptions.selectShopIds) {
      request.selectShopIds = this.inputOptions.selectShopIds;
    } else {
      delete request.selectShopIds;
    }

    return request;
  }
}
